import React, { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import Header from "../components/Header";
import ContactFooter from "../components/ContactFooter";
import BlogGrid from "../components/BlogGrid";
import { getPostBySlug } from "../data/blogPosts";
import { TRUST_PORTAL_URL } from "../data/siteContent";

const renderBody = (body) => {
  const blocks = body.split("\n\n");
  return blocks.map((block, idx) => {
    if (block.startsWith("## ")) {
      return (
        <h2
          key={idx}
          className="font-display text-xl sm:text-2xl text-ink mt-12 mb-4 leading-snug"
        >
          {block.replace("## ", "")}
        </h2>
      );
    }
    if (block.startsWith("### ")) {
      return (
        <h3 key={idx} className="font-display text-lg sm:text-xl text-ink mt-8 mb-3">
          {block.replace("### ", "")}
        </h3>
      );
    }
    if (block.startsWith("- ")) {
      const items = block.split("\n").map((l) => l.replace(/^- /, ""));
      return (
        <ul key={idx} className="list-disc pl-6 space-y-2 text-ink/75 text-base sm:text-lg leading-relaxed mb-6">
          {items.map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ul>
      );
    }
    return (
      <p key={idx} className="text-ink/75 text-base sm:text-lg leading-relaxed mb-6">
        {block}
      </p>
    );
  });
};

const BlogPost = () => {
  const { slug } = useParams();
  const post = getPostBySlug(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!post) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="bg-parchment min-h-screen antialiased">
      <Header light />
      <article className="pt-36 sm:pt-44 pb-20">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-goldeep text-sm font-medium tracking-wide hover:text-ink transition-colors"
            data-testid="blog-back-link"
          >
            <ArrowLeft className="w-4 h-4" /> All Articles
          </Link>

          <time className="block text-ink/45 text-xs uppercase tracking-[0.2em] mt-8">
            {post.date}
          </time>
          <h1 className="font-display text-2xl sm:text-4xl text-ink tracking-tight leading-[1.1] mt-4">
            {post.title}
          </h1>

          <div className="mt-10 rounded-2xl overflow-hidden shadow-lg">
            <img src={post.image} alt={post.title} className="w-full h-64 sm:h-96 object-cover" />
          </div>

          <div className="mt-10">{renderBody(post.body)}</div>

          <div className="mt-14 bg-ink rounded-2xl p-8 sm:p-10 text-center">
            <p className="font-display text-lg sm:text-xl text-cream leading-snug">
              Ready to protect what you built?
            </p>
            <a
              href={TRUST_PORTAL_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-3 bg-gold text-ink font-bold tracking-[0.1em] uppercase text-sm px-8 py-4 rounded-full mt-6 hover:bg-goldlight transition-colors duration-300"
            >
              Get Started
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1.5" />
            </a>
          </div>
        </div>
      </article>
      <BlogGrid title="More Articles" subtitle="Continue reading on trusts, control, and protection." />
      <ContactFooter />
    </div>
  );
};

export default BlogPost;
